import { PublicKey } from '@solana/web3.js';
import {
  ASSOCIATED_TOKEN_PROGRAM_ID,
  DEVNET_USDC_MINT,
  TOKEN_2022_PROGRAM_ID,
  TOKEN_PROGRAM_ID,
} from '../constants/programs.js';

/**
 * Derive the user's Token-2022 Associated Token Account for an outcome token.
 *
 * Seeds: [user, TOKEN_2022_PROGRAM_ID, outcomeMint] via ASSOCIATED_TOKEN_PROGRAM_ID.
 * Outcome tokens use the Token-2022 program (not legacy SPL Token).
 *
 * @param user - User wallet public key
 * @param outcomeMint - Outcome token mint public key
 * @returns ATA public key
 */
export function deriveUserOutcomeAta(user: PublicKey, outcomeMint: PublicKey): PublicKey {
  const [ata] = PublicKey.findProgramAddressSync(
    [user.toBuffer(), TOKEN_2022_PROGRAM_ID.toBuffer(), outcomeMint.toBuffer()],
    ASSOCIATED_TOKEN_PROGRAM_ID,
  );
  return ata;
}

/**
 * Derive the SPL Token Associated Token Account for USDC.
 *
 * Seeds: [owner, TOKEN_PROGRAM_ID, usdMint] via ASSOCIATED_TOKEN_PROGRAM_ID.
 * USDC uses the legacy SPL Token program.
 *
 * @param owner - Owner wallet public key
 * @param usdMint - USDC mint address (defaults to devnet USDC)
 * @returns ATA public key
 */
export function deriveUsdcAta(owner: PublicKey, usdMint: PublicKey = DEVNET_USDC_MINT): PublicKey {
  const [ata] = PublicKey.findProgramAddressSync(
    [owner.toBuffer(), TOKEN_PROGRAM_ID.toBuffer(), usdMint.toBuffer()],
    ASSOCIATED_TOKEN_PROGRAM_ID,
  );
  return ata;
}

/**
 * Derive the Token-2022 vault for an outcome token held by the mint authority.
 *
 * This is the "token_vault" account in buy/sell instructions — the vault that
 * holds pre-minted outcome tokens owned by the market's mint authority PDA.
 *
 * Seeds: [mintAuthority, TOKEN_2022_PROGRAM_ID, outcomeMint] via ASSOCIATED_TOKEN_PROGRAM_ID.
 *
 * @param mintAuthority - Mint authority PDA for the market
 * @param outcomeMint - Outcome token mint (Token-2022)
 * @returns ATA public key for the outcome token vault
 */
export function deriveTokenVault(mintAuthority: PublicKey, outcomeMint: PublicKey): PublicKey {
  return deriveUserOutcomeAta(mintAuthority, outcomeMint);
}

/**
 * Derive the SPL Token (legacy, NOT Token-2022) Associated Token Account where
 * the bonding curve's outcome-token reserves live. The owner is the `market`
 * PDA itself — `allowOwnerOffCurve` is implicit since findProgramAddressSync
 * accepts any 32-byte key as `owner` (PDAs are valid ATA owners).
 *
 * Used by `init_outcome` as `bc_token_vault`. Mirrors the devnet-bot at
 * `backend/devnet-bots-service/src/v2-market-bootstrapper.ts:499`:
 *   `getAssociatedTokenAddressSync(outcomeMint, market, true)`
 */
export function deriveBcTokenVaultAta(market: PublicKey, outcomeMint: PublicKey): PublicKey {
  const [ata] = PublicKey.findProgramAddressSync(
    [market.toBuffer(), TOKEN_PROGRAM_ID.toBuffer(), outcomeMint.toBuffer()],
    ASSOCIATED_TOKEN_PROGRAM_ID,
  );
  return ata;
}
